import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FiSearch, FiShoppingCart, FiHeart, FiUser, FiMenu, FiX, FiPackage, FiGift, FiPhone, FiTrendingUp, FiSettings, FiLogOut } from 'react-icons/fi';
import { useShopping } from '../context/ShoppingContext';
import { auth } from '../utils/firebase';
import apiClient from '../utils/apiClient';
import '../styles/Header.css';

const Header = () => {
  const { getCartCount, user } = useShopping();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [categories, setCategories] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');

  // Fetch categories for dynamic navigation
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await apiClient.get('/trending/sections');
        // Filter out 'featured' as it's not a shop category
        const shopCategories = (response.data.sections || []).filter(s => s.id !== 'featured');
        setCategories(shopCategories);
      } catch (error) {
        console.error('Error fetching categories:', error);
        // Fallback categories
        setCategories([
          { id: 'stationary', name: 'Stationary' },
          { id: 'textiles', name: 'Textiles & Fabrics' },
          { id: 'pottery', name: 'Pottery & Ceramics' },
          { id: 'handicrafts', name: 'Handicrafts' },
          { id: 'electronics', name: 'Electronics' },
          { id: 'home-decor', name: 'Home Decor' }
        ]);
      }
    };

    if (!isAdminRoute) {
      fetchCategories();
    }
  }, [isAdminRoute]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // Navigate to products page with search query
      navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
      setIsMenuOpen(false);
    }
  };

  const handleLogout = async () => {
    try {
      await auth.signOut();
      navigate('/account');
      setIsProfileOpen(false);
      setIsMenuOpen(false);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const toggleProfile = (e) => {
    e.preventDefault();
    setIsProfileOpen(!isProfileOpen);
    setIsMenuOpen(false); // Close mobile menu if open
  };

  const closeMenus = () => {
    setIsProfileOpen(false);
    setIsMenuOpen(false);
  };

  return (
    <header className="header">
      <div className="header-top">
        <div className="header-container">
          <Link to="/" className="logo" onClick={closeMenus}>
            <img src="/logo.png" alt="Kalinga Crafts Store" className="logo-image" />
            <span className="logo-text">Kalinga Crafts Store</span>
          </Link>

          <form className="search-bar" onSubmit={handleSearch}>
            <input
              type="text"
              placeholder="Search for products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <button type="submit" className="search-btn">
              <FiSearch size={20} />
            </button>
          </form>

          <div className="header-icons">
            <Link to="/wishlist" className="header-icon" onClick={closeMenus}>
              <FiHeart size={24} />
              <span className="icon-label">Wishlist</span>
            </Link>

            <div className="profile-container">
              <div className="header-icon" onClick={toggleProfile}>
                <FiUser size={24} />
                <span className="icon-label">Profile</span>
              </div>

              <div className={`profile-dropdown ${isProfileOpen ? 'active' : ''}`}>
                <div className="profile-header">
                  <h3>Hello {user ? (user.displayName || 'User') : 'Guest'}</h3>
                  {user ? (
                    <p className="user-contact">{user.email || user.phoneNumber}</p>
                  ) : (
                    <p className="welcome-text">Welcome to Kalinga Crafts</p>
                  )}
                </div>
                <div className="profile-menu">
                  {!user ? (
                    <div className="auth-buttons">
                      <Link to="/account" className="menu-btn login-btn" onClick={closeMenus}>Login / Signup</Link>
                    </div>
                  ) : null}
                  <Link to="/orders" className="menu-item" onClick={closeMenus}>
                    <FiPackage size={18} />
                    <span>Orders</span>
                  </Link>
                  <Link to="/wishlist" className="menu-item" onClick={closeMenus}>
                    <FiHeart size={18} />
                    <span>Wishlist</span>
                  </Link>
                  <Link to="/gift-cards" className="menu-item" onClick={closeMenus}>
                    <FiGift size={18} />
                    <span>Gift Cards</span>
                  </Link>
                  <Link to="/contact" className="menu-item" onClick={closeMenus}>
                    <FiPhone size={18} />
                    <span>Contact Us</span>
                  </Link>
                  <Link to="/seller/register" className="menu-item" onClick={closeMenus}>
                    <FiTrendingUp size={18} />
                    <span>Become a Seller</span>
                  </Link>
                  {user && (
                    <>
                      <div className="menu-divider"></div>
                      <Link to="/account" className="menu-item" onClick={closeMenus}>
                        <FiSettings size={18} />
                        <span>Edit Profile</span>
                      </Link>
                      <button onClick={handleLogout} className="menu-item logout-btn">
                        <FiLogOut size={18} />
                        <span>Logout</span>
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>

            <Link to="/cart" className="header-icon cart-icon" onClick={closeMenus}>
              <FiShoppingCart size={24} />
              {getCartCount() > 0 && <span className="cart-count">{getCartCount()}</span>}
              <span className="icon-label">Cart</span>
            </Link>
          </div>

          <button
            className="menu-toggle"
            onClick={() => {
              setIsMenuOpen(!isMenuOpen);
              setIsProfileOpen(false);
            }}
          >
            {isMenuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
          </button>
        </div>
      </div>

      <nav className={`navbar ${isMenuOpen ? 'active' : ''}`}>
        {isAdminRoute ? (
          <>
            <Link to="/admin" onClick={closeMenus}>
              Dashboard
            </Link>
            <Link to="/admin/products" onClick={closeMenus}>
              Products
            </Link>
            <Link to="/admin/orders" onClick={closeMenus}>
              Orders
            </Link>
            <Link to="/admin/shipments" onClick={closeMenus}>
              Shipments
            </Link>
            <Link to="/admin/coupons" onClick={closeMenus}>
              Offers & Coupons
            </Link>
            <Link to="/admin/team" onClick={closeMenus}>
              Team
            </Link>
            <Link to="/admin/trending" onClick={closeMenus}>
              Trending
            </Link>
            <Link to="/admin/categories" onClick={closeMenus}>
              Categories
            </Link>
          </>
        ) : (
          <>
            <Link to="/" onClick={closeMenus}>
              Home
            </Link>
            <Link to="/products" onClick={closeMenus}>
              All Products
            </Link>
            {categories.map(category => (
              <Link
                key={category.id}
                to={`/shop/${category.id}`}
                onClick={closeMenus}
              >
                {category.name}
              </Link>
            ))}
          </>
        )}
      </nav>
    </header>
  );
};

export default Header;
